import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addProcessIntermediateWasteGeneratedToProduct,
  getProcessIntermediateWasteGeneratedList,
  updateProcessIntermediateWasteGenerated,
  deleteProcessIntermediateWasteGenerated,
} from "../../../../../../apis/Product/ProcessIntermediateSoldWasteGeneratedApi";
// import { getProdSurplusResourcesDropdown } from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { useBonsaiData } from "../../../../../../hooks/useBonsaiData";
import { getLocationDropdownLCA } from "../../../../../../apis/Bonsai/locationDropdownApi";
import TipBox from "../../../../../../components/TipBox";

const WasteGeneratedTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [dropdownLocation, setDropdownLocation] = useState([]);

  // Use cached Bonsai data instead of individual API call
  const { data: dropdownProcess, isLoading: isBonsaiLoading } = useBonsaiData();

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProcessIntermediateWasteGeneratedList({
      productId,
      setObjects: setData,
    });
    getLocationDropdownLCA({
      setDropdownData: setDropdownLocation,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["current_disposal"]?.value || row.current_disposal;
        if (materialValue) {
          const selectedMaterial = dropdownProcess.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownProcess]);

  const dynamicPlaceholders = useMemo(
    () => ({
      current_disposal: {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit,
              }));
            }
          }
        },
        dropdownDataKey: "current_disposal",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const foundProduct = dropdownProcess?.find(
        (option) => option.name === editedItem.current_disposal.value
      );
      const product_code = foundProduct
        ? foundProduct["code"]
        : editedItem.product_code;

      const foundLocation = dropdownLocation?.find(
        (option) => option.name === editedItem["location"].value
      );

      const locationCode = foundLocation.code;
      updateProcessIntermediateWasteGenerated({
        productId,
        componentId: editedItem.id,
        ...editedItem,
        current_disposal: editedItem.current_disposal.value,
        objects: data,
        setObjects: setData,
        use_swal: true,
        product_code: product_code,
        location_code: locationCode,
        location: editedItem["location"].value,
      });
    },
    [productId, data, setData, dropdownProcess, dropdownLocation]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const foundProduct = dropdownProcess?.find(
        (option) => option.name === newItem.current_disposal.value
      );

      const product_code = foundProduct ? foundProduct["code"] : "";
      const foundLocation = dropdownLocation?.find(
        (option) => option.name === newItem["location.value"].value
      );
      const locationCode = foundLocation.code;
      addProcessIntermediateWasteGeneratedToProduct({
        productId,
        ...newItem,
        current_disposal: newItem.current_disposal.value,
        callback: fetchData,
        use_swal: true,
        product_code: product_code,
        location_code: locationCode,
        location: newItem["location.value"].value,
      });
    },
    [productId, fetchData, dropdownProcess, dropdownLocation]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProcessIntermediateWasteGenerated({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "current_disposal",
        header: `${t("Final treatment method")}`,
        type: "dropdown",
        dropdownData: dropdownProcess,
        disableSortBy: true,
        width: "90px",
        tipHeader: "Tip",
        tipBody: t("WASTE_GEN_TIPBOX_MESSAGE"),
      },
      {
        accessor: "qty",
        header: `${t("QUANTITY")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: (rowId) => {
          return `${unitMaterials[rowId] || "xx"}`;
        },
      },
      {
        accessor: "location.value",
        header: t("PRODUCTION_COUNTRY"),
        type: "dropdown",
        dropdownData: dropdownLocation,
        disableSortBy: true,
        width: "90px",
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [dropdownProcess, unitMaterials, dropdownLocation, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      current_disposal: dropdownProcess,
      "location.value": dropdownLocation,
    }),
    [dropdownProcess, dropdownLocation]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default WasteGeneratedTable;
